import React, { useState, useEffect} from 'react';
import '../PreApprovalForm/PreApprovalForm.scss';
import './ProcessingTeamForm.scss';
import { TextInput } from '../../atoms/TextInput/TextInput';
import { FooterContent } from '../ResumeFooter/ResumeFooter';
import { AddressCaptionInner, formatPhoneNumber, validatePhoneNumber } from '../PreApprovalForm/PreApprovalForm';
import { InputAutocompleteAddress } from '../../atoms/TextInput/TextInput';
import { OptionDropdownInner } from '../../atoms/OptionDropdown/OptionDropdown';
import { useGetBorrowers } from '../../../services/borrowers';



export const ProcessingTeamForm = (props) => {
  const { values, onContinue, borrowerData, setBorrowerData } = props;

  const [firstName, setFirstName] = useState(() => borrowerData?.name || "")
  const [lastName, setLastName] = useState(() => borrowerData?.lastName || "")
  const [email, setEmail] = useState(() => borrowerData?.email || "")
  const [phoneNumber, setPhoneNumber] = useState(() => borrowerData?.phone || "")
  const [password] = useState("password")

  const [investmentAddress, setInvestmentAddress] = useState()
  const [errors, setErrors] = useState({
    firstName: null,
    lastName: null,
    email: null,
    phoneNumber: null
  })

  const { data } = useGetBorrowers()
  const [selectedBorrowerEmail, setSelectedBorrowerEmail] = useState(() => {
    if (borrowerData) {
      return borrowerData.email
    } else if (borrowerData === null) {
      return "new"
    }
    // If borrowerData is undefined, it means no selection has been made yet
    // Don't set a default value, let the user choose
    return null
  })
  const [filterValue, setFilterValue] = useState(null)
  const [isExistingBorrower, setIsExistingBorrower] = useState(!!borrowerData)

  const borrowers = data?.getBorrowers?.filter((r) => r.name) || []

  let options = [
    ...borrowers
      .map((v) => {
        return {
          value: v.email,
          label: `${v.name} ${v.lastName}`,
          disabled: false,
          className: undefined,
          raw: v
        }
      })
  ]

  const defaultValue = options[0]

  if (filterValue) {
    options = options.filter((o) => o.label.toLowerCase().includes(filterValue.toLowerCase()))
  }

  options.push(
    {
      value: "new",
      label: "New Borrower",
      disabled: false,
      className: 'new-borrower',
      raw: null
    }
  )

  const userData = {
    firstName,
    lastName,
    email,
    password,
    phoneNumber,
    investmentAddress,
    isNewBorrower: !isExistingBorrower
  }

  const [addressSelected, setAddressSelected] = useState(false)

  const clearAllErrors = () => {
    setErrors({
      firstName: null,
      lastName: null,
      email: null,
      phoneNumber: null
    });
  };

  const validateFirstName = (value) => {
    if (!value || value.trim() === "") return "First name is required";
    return null;
  };

  const validateLastName = (value) => {
    if (!value || value.trim() === "") return "Last name is required";
    return null;
  };

  const validateEmail = (value) => {
    if (!value || value.trim() === "") return "Email address is required";
    const emailRegex = /\S+@\S+\.\S+/;
    if (!emailRegex.test(value)) return "Invalid email address";
    return null;
  };

  const isFormValid = () => {
    if (isExistingBorrower) return true;
    
    const firstNameError = validateFirstName(firstName);
    const lastNameError = validateLastName(lastName);
    const emailError = validateEmail(email);
    const phoneError = validatePhoneNumber(phoneNumber);
    
    return !firstNameError && !lastNameError && !emailError && !phoneError;
  };


  useEffect(() => {
    if (borrowerData) {
      setSelectedBorrowerEmail(borrowerData.email)
      setIsExistingBorrower(true)
      setFirstName(borrowerData.name || "")
      setLastName(borrowerData.lastName || "")
      setEmail(borrowerData.email || "")
      setPhoneNumber(borrowerData.phone || "")
    } else {
      setSelectedBorrowerEmail("new")
      setIsExistingBorrower(false)
      setFirstName("")
      setLastName("")
      setEmail("")
      setPhoneNumber("")
    }
    clearAllErrors()
  }, [borrowerData])


  const onBorrowerChange = (v) => {
    setSelectedBorrowerEmail(v)
    const selected = options.find((o) => o.value === v)?.raw || null
    
    if (selected) {
      setFirstName(selected.name || "")
      setLastName(selected.lastName || "")
      setEmail(selected.email || "")
      setPhoneNumber(selected.phone || "")
      setIsExistingBorrower(true)
      setBorrowerData(selected)
    } else {
      setFirstName("")
      setLastName("")
      setEmail("")
      setPhoneNumber("")
      setIsExistingBorrower(false)
      setBorrowerData(null)
    }
    clearAllErrors()
  }

  const handleFirstNameChange = (value) => {
    if (isExistingBorrower) return;
    setFirstName(value);
    const error = validateFirstName(value);
    setErrors(prev => ({ ...prev, firstName: error }));
  };

  const handleLastNameChange = (value) => {
    if (isExistingBorrower) return;
    setLastName(value);
    const error = validateLastName(value);
    setErrors(prev => ({ ...prev, lastName: error }));
  };

  const handleEmailChange = (value) => {
    if (isExistingBorrower) return;
    setEmail(value);
    const error = validateEmail(value);
    setErrors(prev => ({ ...prev, email: error }));
  };

  const handlePhoneNumberChange = (value) => {
    if (isExistingBorrower) return;
    
    const formattedValue = formatPhoneNumber(value);
    setPhoneNumber(formattedValue);
    
    const phoneError = validatePhoneNumber(formattedValue);
    setErrors(prev => ({
      ...prev,
      phoneNumber: phoneError
    }));
  }

  const titleMarkup = (
    borrowerData ?
    <h1>Confirm the account info and we'll add the loan to the account</h1>
    :
    <h1>Fill out borrower info and generate account creation email</h1>
  )

  return (
    <div className='pre-approval-form processing'>
      <div className='first-section'>
        <p>
          New loan for {" "}
          <div style={{ display: 'inline-block' }}>
            <OptionDropdownInner
              isProcessingTeamMode={true}
              onSearch={(s) => setFilterValue(s)}
              options={options}
              defaultValue={defaultValue}
              value={selectedBorrowerEmail}
              onChange={onBorrowerChange}
            />
          </div>
        </p>
        {titleMarkup}
        <div className='inputs-container'>
          <TextInput label={"First name"} value={firstName} onChange={handleFirstNameChange} error={errors.firstName} />
          <TextInput label={"Last name"} value={lastName} onChange={handleLastNameChange} error={errors.lastName} />
          <TextInput label={"Email address"} value={email} onChange={handleEmailChange} error={errors.email} />
          <TextInput label={"Phone number"} value={phoneNumber} onChange={handlePhoneNumberChange} error={errors.phoneNumber} />
        </div>

        <div className='property'>
          <InputAutocompleteAddress
            onSelect={({ formatted_address }) => {
              const addressWithoutCountry = formatted_address.replace(/, [^,]*$/, "");
              setInvestmentAddress(addressWithoutCountry);
            }}
          />
          <AddressCaptionInner {
            ...{ addressSelected, setAddressSelected, caption: "The borrower doesn't have specific property in mind" }
          } />
        </div>
      </div>

      {
        !addressSelected ?
          <div className='resume-container'>
            <FooterContent values={values} oneLine={true} isSpan={true} isProcessingTeamMode={true} />
          </div>
          :
          null
      }

      <div>
        <div 
          className={`loan-options-button ${!isFormValid() ? 'disabled' : ''}`} 
          onClick={isFormValid() ? () => onContinue(userData) : undefined}
        >
          Email borrower account creation link
        </div>
        <span className='caption'>Borrower agrees to be contacted by Constitution Lending</span>
      </div>
    </div>
  );
}